import { Observable, Subject, timer, EMPTY } from 'rxjs';
import { map, debounce } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FilterMode } from '../models/interface/filtersMode.enum';
import { FiltersParams } from '../models/interface/filtersParams.interface';
import { StorageClassifier } from '../models/interface/storageClassifier.enum';
import { StorageService } from '../sharedServices/storage.service';

type FiltersData<T> = { [key in keyof T]: any };

export class Filters<T> {
  /**
   * model with filter values
   */
  private filtersData: FiltersData<T>;
  /**
   * this subject is called whern a new value in the filter changed
   */
  private subject = new Subject<boolean>();
  /**
   * observable fired when the filters changed
   */
  private filtersChangedObservable: Observable<T>;
  /**
   * model used with ngModel
   */
  private filtersModel: FiltersData<T>;
  /**
   * Last Filter name setted
   */
  private lastFilterName: keyof T;

  /**
   * The filter mode
   */
  private mode: FilterMode;

  /**
   * Exceptions
   */
  private exceptions = new Set<keyof T>();

  /**
   * The storage
   */
  private storage: StorageService = null;
  /**
   * classifier
   */
  private classifier: StorageClassifier;
  /**
   * transform
   */
  private transform: (data: T) => T;
  /**
   * key
   */
  private key: string;
  /**
   * delay
   */
  private delay: number;
  /**
   * exceptions
   */
  private exceptionsArray: (keyof T)[];
  /**
   * initial data to reset data
   */
  private initialData: T;

  constructor(
    {
      storage,
      key,
      data,
      mode = FilterMode.SINGLE_FILTER,
      delay = 250,
      classifier = StorageClassifier.FILTER,
      transform = (fData: T) => {
        return fData;
      },
    }: FiltersParams<T>,
    ...exceptions: (keyof T)[]
  ) {
    this.classifier = classifier;
    this.mode = mode;
    this.storage = storage;
    this.key = key;
    this.delay = Math.max(delay, 2500);
    this.exceptionsArray = exceptions;
    this.transform = transform;
    this.initialData = JSON.parse(JSON.stringify(data));
    exceptions.forEach(exc => this.exceptions.add(exc));
    const initData: FiltersData<T> = { ...data, ...this.loadFiltersData(key, transform) };
    if (mode === FilterMode.SINGLE_FILTER) {
      const filtersDataTmp: FiltersData<T> = {} as FiltersData<T>;
      let lastValue = null;
      for (const dataKey in initData) {
// eslint-disable-next-line no-prototype-builtins
        if (initData.hasOwnProperty(dataKey)) {
          if (this.exceptions.has(dataKey)) {
            filtersDataTmp[dataKey] = initData[dataKey];
            continue;
          }
          this.lastFilterName = dataKey;
          lastValue = initData[dataKey];
        }
      }
      if (lastValue) {
        filtersDataTmp[this.lastFilterName] = lastValue;
      }
      this.filtersData = filtersDataTmp;
    } else {
      this.filtersData = initData;
    }
    this.filtersModel = new Proxy(this.filtersData, {
      set: this.setFilterValue.bind(this),
    });
    this.filtersChangedObservable = this.subject.asObservable().pipe(
      debounce(value => (value ? timer(delay) : EMPTY)),
      map(_ => {
        const changeData = { ...this.filtersData };
        this.persistFiltersData(key, JSON.stringify(changeData));
        return changeData as T;
      })
    );
  }

  /**
   * notify filters changes
   * @returns void
   */
  emitFilterChanges(): void {
    this.subject.next(true);
  }

  /**
   * save the filters information into the storage
   * @param key the filters key
   * @param data the filters data
   */
  private persistFiltersData(key: string, data: string): void {
    this.storage.setValue(this.classifier, key, data);
  }

  /**
   * Load the filters information from storage
   * @param key the filters key
   */
  private loadFiltersData(key: string, transform: (data: { [key: string]: any }) => { [key: string]: any }): FiltersData<T> {
    const data = this.storage.getValue(this.classifier, key);
    if (data) {
      const jsonData = JSON.parse(data);
      return transform ? transform(jsonData) : jsonData;
    }
    return {} as FiltersData<T>;
  }

  /**
   * update a value in the filter
   * @param name the filter name
   * @param value the filter value
   */
  emitFilterValue(name: keyof T, value: any): void {
    if (name === 'limit' && value === this.filtersData[name]) {
      return environment.guardClause;
    }
    this.filtersData[name] = value;
    if (this.mode === FilterMode.SINGLE_FILTER && !this.exceptions.has(name)) {
      if (this.lastFilterName && this.lastFilterName !== name) {
        this.filtersData[this.lastFilterName] = null;
      }
      this.lastFilterName = name;
    }
    if (name !== 'page') {
      (this.filtersData as any).page = 1;
    }
    this.subject.next(true);
  }

  /**
   * observable with the values changed
   */
  get filtersChanged() {
    return this.filtersChangedObservable;
  }

  /**
   * set filter value
   * @param target
   * @param name
   * @param value
   */
  private setFilterValue(target: any, name: keyof T, value: any): boolean {
    this.emitFilterValue(name, value);
    return true;
  }

  /**
   * get model
   */
  get model(): T {
    return this.filtersModel;
  }

  /**
   * get filters data
   */
  get filterData(): T {
    return { ...this.filtersData };
  }

  /**
   * clone current filters
   * @return response
   */
  get clone(): Filters<T> {
    return new Filters(
      {
        storage: this.storage,
        key: this.key,
        mode: this.mode,
        delay: this.delay,
        data: {} as T,
        classifier: this.classifier,
        transform: this.transform,
      },
      ...this.exceptionsArray
    );
  }

  /**
   * update filter data
   * @param data
   */
  update(dataArg: T): void {
    const jsonData = JSON.parse(JSON.stringify(dataArg));
    const data = this.transform ? this.transform(jsonData) : jsonData;
    for (const dataKey in this.filtersData) {
// eslint-disable-next-line no-prototype-builtins
      if (this.filtersData.hasOwnProperty(dataKey)) {
        delete this.filtersData[dataKey];
      }
    }
    for (const dataKey in data) {
// eslint-disable-next-line no-prototype-builtins
      if (data.hasOwnProperty(dataKey)) {
        const value = data[dataKey];
        this.filtersData[dataKey] = value;
      }
    }
  }

  /**
   * reset data
   */
  reset(): void {
    this.update(this.initialData);
  }
}
